import { useCallback, useEffect } from 'react';
import { isEmpty } from 'lodash';
import styled from 'styled-components';
import { NavBar, NavBarList, spacer32 } from '@spotify-internal/encore-web';
import { useT } from '@spotify-internal/i18n-core';
import { sendEvent } from '@spotify-internal/s4mp-page-analytics';
import { ArtistAlbum } from '@hooks/useArtistAppearsOn/types';
import { AlbumsPaginationContext } from '@hooks/useArtistInfo/types';
import { RenderPaginationProps } from '@hooks/usePagination';
import { useTabsWithQueryParams } from '@hooks/useTabsWithQueryParams';
import { NavBarListItemAsLink } from '@components/shared/NavBarListItemAsLink';
import { EVENT_ACTION, EVENT_CATEGORY } from '@lib/ga/types';
import { GA_PRODUCT_CATALOG } from '@helpers/constants';
import { focusActiveTab } from '@helpers/focusActiveTab';
import { AppearsOnContainer } from '../AppearsOn/AppearsOnContainer';
import { ReleasesTable } from '../Releases/ReleasesTable';

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${spacer32};
`;

const RELEASES = 'releases';
const APPEARS_ON = 'appearsOn';

type PageTabs = typeof RELEASES | typeof APPEARS_ON;

const ALL_PAGE_TABS: PageTabs[] = [RELEASES, APPEARS_ON];

type NavBarListItem = {
  id: string;
  label: string | JSX.Element;
  gaEventLabel: string;
  href: string;
  component: JSX.Element;
};
interface ArtistOverviewTabsProps {
  artistName: string;
  artistUri: string;
  albums: ArtistAlbum[];
  paginationProps: {
    albumsPaginationContext: AlbumsPaginationContext;
    renderArtistReleasesPagination: (
      renderArtistReleasesPaginationProps: RenderPaginationProps,
    ) => JSX.Element;
  };
}

export const ArtistOverviewTabs = ({
  artistName,
  artistUri,
  albums,
  paginationProps,
}: ArtistOverviewTabsProps) => {
  const {
    activePageTab,
    actions: { buildTabParam },
  } = useTabsWithQueryParams<PageTabs>(RELEASES, ALL_PAGE_TABS);

  const t = useT();

  const RELEASES_PARAM = buildTabParam(RELEASES);
  const APPEARS_ON_PARAM = buildTabParam(APPEARS_ON);

  const navBarListItems: NavBarListItem[] = [
    {
      id: RELEASES,
      gaEventLabel: 'Releases table',
      label: t('atlas.releases', 'Releases', 'Text for Releases (plural)'),
      href: RELEASES_PARAM,
      component: (
        <ReleasesTable
          variant="releases"
          showEmptyState={isEmpty(albums)}
          albums={albums}
          paginationProps={{
            paginationContext: paginationProps.albumsPaginationContext,
            renderPagination: paginationProps.renderArtistReleasesPagination,
          }}
        />
      ),
    },
    {
      id: APPEARS_ON,
      gaEventLabel: 'Appears on',
      href: APPEARS_ON_PARAM,
      label: t(
        'atlas.labels.appears_on',
        'Appears on',
        'Label for section indicating which products an artist appears on',
      ),
      component: (
        <AppearsOnContainer artistName={artistName} artistUri={artistUri} />
      ),
    },
  ];

  const onBackArrow = () => {
    focusActiveTab();
  };
  const handlePopStateChange = useCallback(() => {
    setTimeout(() => onBackArrow(), 0);
  }, []);
  useEffect(() => {
    window.addEventListener('popstate', handlePopStateChange);
    return () => {
      window.removeEventListener('popstate', handlePopStateChange);
    };
  }, [handlePopStateChange]);

  return (
    <>
      <HeaderContainer>
        <NavBar
          list={
            <NavBarList>
              {navBarListItems.map(item => (
                <NavBarListItemAsLink
                  sentenceCase
                  active={item.id === activePageTab}
                  key={`tab-${item.id}`}
                  href={item.href}
                  label={item.label}
                  id={item.id}
                  aria-controls={item.id}
                  onClick={() => {
                    sendEvent({
                      eventCategory: EVENT_CATEGORY.ARTIST_PAGE,
                      eventAction: EVENT_ACTION.CHANGE_NAV_PILL,
                      eventLabel: item.gaEventLabel,
                      product: GA_PRODUCT_CATALOG,
                    });
                  }}
                />
              ))}
            </NavBarList>
          }
        />
      </HeaderContainer>
      {
        navBarListItems.find(listItem => listItem?.id === activePageTab)!
          .component
      }
    </>
  );
};
